// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

// Service Worker Configuration
const SW_VERSION = '1.1.2';
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour in milliseconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 5000; // 5 seconds in milliseconds

const isLocalhost = Boolean( // eslint-disable-line no-unused-vars
  window.location.hostname === 'localhost' ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === '[::1]' ||
    // 127.0.0.0/8 are considered localhost for IPv4.
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/)
);

// Environment detection (consistent with main utility)
const isElectron = () => {
  return window.electronAPI && typeof window.electronAPI === 'object';
};

const isVisible = () => {
  if (typeof document !== 'undefined') {
    return document.visibilityState === 'visible';
  }
  return true;
};

const isOnline = () => {
  if (typeof navigator !== 'undefined') {
    return navigator.onLine;
  }
  return true;
};

const isProduction = () => {
  return process.env.NODE_ENV === 'production';
};

const isServiceWorkerSupported = () => {
  return 'serviceWorker' in navigator;
};

const shouldRegisterServiceWorker = () => {
  // Never register service worker in Electron - it handles updates natively
  if (isElectron()) {
    console.log('[ServiceWorker] Electron detected - service worker not needed');
    return false;
  }

  // Register in production web environment when supported; allow offline startup
  return isProduction() && isServiceWorkerSupported();
};

export function register(config) {
  if (shouldRegisterServiceWorker()) {
    console.log('[ServiceWorker] Registering service worker for web environment');
    // The URL constructor is available in all browsers that support SW.
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // Our service worker won't work if PUBLIC_URL is on a different origin
      // from what our page is served on. This might happen if a CDN is used to
      // serve assets; see https://github.com/facebook/create-react-app/issues/2374
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      // Register service worker regardless of localhost; allow offline
      registerValidSW(swUrl, config);
    });
  } else {
    console.log('[ServiceWorker] Service worker registration skipped:', {
      isElectron: isElectron(),
      isProduction: isProduction(),
      isServiceWorkerSupported: isServiceWorkerSupported(),
      isVisible: isVisible(),
      isOnline: isOnline()
    });
  }
}

function registerValidSW(swUrl, config) {
  let retryCount = 0;
  let updateCheckInterval = null;
  let onlineRetryAttached = false;

  const attemptRegistration = () => {
    // Add version to URL to prevent caching issues
    const versionedSwUrl = `${swUrl}?v=${SW_VERSION}`;

    // Register the service worker without clearing caches or unregistering existing SWs
    navigator.serviceWorker.register(versionedSwUrl, {
      scope: '/',
      updateViaCache: 'none'
    }).then((registration) => {
      // Force immediate update check
      registration.update();

      // Clear any existing update check interval
      if (updateCheckInterval) {
        clearInterval(updateCheckInterval);
      }

      // Set up hourly update checks
      updateCheckInterval = setInterval(() => {
        registration.update();
      }, UPDATE_CHECK_INTERVAL);

      // Listen for updates
      registration.addEventListener('updatefound', () => {
        const installingWorker = registration.installing;
        if (installingWorker == null) {
          return;
        }

        installingWorker.addEventListener('statechange', () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // At this point, the updated precached content has been fetched,
              // but the previous service worker will still serve the older
              // content until all client tabs are closed.
              console.log(
                'New content is available and will be used when all ' +
                  'tabs for this page are closed. See https://cra.link/PWA.'
              );

              // Execute callback
              if (config && config.onUpdate) {
                config.onUpdate(registration);
              }
            } else {
              // At this point, everything has been precached.
              // It's the perfect time to display a
              // "Content is cached for offline use." message.
              console.log('Content is cached for offline use.');

              // Execute callback
              if (config && config.onSuccess) {
                config.onSuccess(registration);
              }
            }
          }
        });
      });

      // Listen for controller change
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        console.log('New service worker activated');
        if (config && config.onUpdate) {
          config.onUpdate(registration);
        }
      });
    }).catch((error) => {
      console.error('Error during service worker registration:', error);
      
      // Retry registration if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        retryCount++;
        console.log(`Retrying service worker registration (${retryCount}/${MAX_RETRIES})...`);
        setTimeout(attemptRegistration, RETRY_DELAY * retryCount);
      } else {
        console.error('Service worker registration failed after maximum retries');
        // If offline, attach an online listener to try again once
        if (!onlineRetryAttached && typeof window !== 'undefined') {
          onlineRetryAttached = true;
          const onOnline = () => {
            window.removeEventListener('online', onOnline);
            retryCount = 0;
            attemptRegistration();
          };
          window.addEventListener('online', onOnline);
        }
      }
    });
  };

  attemptRegistration();
}

// Deprecated localhost validator removed; we register directly and handle errors.

export function unregister() {
  if (isServiceWorkerSupported() && !isElectron()) {
    console.log('[ServiceWorker] Unregistering service worker');
    navigator.serviceWorker.ready
      .then((registration) => {
        registration.unregister();
      })
      .catch((error) => {
        console.error(error.message);
      });
  } else {
    console.log('[ServiceWorker] Service worker unregistration skipped - Electron detected');
  }
}
